import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Launch promotion: new listers get started free until August 1, 2026. This
// mirrors the backend fee waiver (listing_routes.py LAUNCH_PROMO_FREE_UNTIL /
// _fee_exempt). Everything auto hides the moment the promo ends so stale copy
// never lingers.
export const PROMO_END = new Date("2026-08-01T00:00:00-04:00"); // Aug 1, 2026 (ET)
const SEEN_KEY = "swiprent_promo_seen";

export function promoActive() {
  return Date.now() < PROMO_END.getTime();
}

// Inline contextual banner (used inside Pricing / lister dashboard, NOT on the
// main browse or swipe surfaces). Dash free copy per brand rules.
export default function PromoBanner({ className = "" }) {
  if (!promoActive()) return null;
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 rounded-2xl bg-brand-primary px-4 py-2.5 text-center text-sm font-semibold text-white shadow-sm",
        className
      )}
    >
      <Sparkles size={16} className="shrink-0" />
      <span>
        Launch offer: list your place{" "}
        <span className="underline underline-offset-2">free</span> until August 1, 2026. No listing fees for owners or realtors.
      </span>
    </div>
  );
}

// One time popup shown once per browser near first load. Replaces the old top
// banner on the main and swipe pages.
export function PromoPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!promoActive()) return undefined;
    let seen = false;
    try {
      seen = window.localStorage.getItem(SEEN_KEY) === "1";
    } catch {
      seen = false;
    }
    if (seen) return undefined;
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, []);

  function dismiss() {
    setOpen(false);
    try {
      window.localStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* private mode: just close for this session */
    }
  }

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-5"
      role="dialog"
      aria-modal="true"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl border border-night-line bg-night-surface p-7 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          className="absolute right-4 top-4 text-night-sub hover:text-night-text"
        >
          <X size={20} />
        </button>
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-primary text-white">
          <Sparkles size={26} />
        </span>
        <h2 className="mt-4 text-xl font-extrabold tracking-tight text-night-text">Launch offer</h2>
        <p className="mt-2 text-sm leading-relaxed text-night-sub">
          List your place free until August 1, 2026. No listing fees for owners or realtors. Get your home in front of renters today.
        </p>
        <button
          type="button"
          onClick={dismiss}
          className="mt-6 w-full rounded-xl bg-brand-primary py-3 text-sm font-extrabold text-white transition hover:bg-brand-primary-hover"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
